import { useCallback, useEffect, useMemo, useState } from 'react';
import type { useRouter } from 'expo-router';

import { ROUTES } from '../../../navigation/routes';
import { billingAPI, getBillingCheckoutConflict } from '../../../services/api';
import type { PlanChangePreview } from '../../../services/api';
import { trackAuthEvent } from '../../../services/analytics';
import { reportHandledError } from '../../../services/errorReporting';
import { confirmDialog, showDialog } from '../../../utils/dialogs';
import {
  PLAN_ALLOWED_BILLING_CYCLES,
  normalizeBillingCycle,
  normalizeBillingCycleForTier,
  normalizePlanTier,
} from '../planBilling';
import { formatCurrencyAmount, formatDate, planDisplayName } from '../profileFormat';
import { describePlanChange, fallbackPlanChangeCopy } from '../planChangePreview';
import { PLAN_DETAILS_URL, openExternalLink } from '../profileLinks';
import type {
  BillingCycleOption,
  PlanPricingMap,
  PlanPricingStatus,
  PlanTier,
  Translate,
  UserProfile,
} from '../profileTypes';

interface UseChangePlanOptions {
  profile: UserProfile | null;
  loadProfile: () => Promise<void>;
  refreshAuth: () => Promise<unknown>;
  router: ReturnType<typeof useRouter>;
  language: string;
  t: Translate;
}

/**
 * Change-plan modal: plan/cycle form, lazy pricing load, submit, and the
 * dead-subscription redirect to checkout (shared with resume).
 */
export function useChangePlan({ profile, loadProfile, refreshAuth, router, language, t }: UseChangePlanOptions) {
  const [showChangePlan, setShowChangePlan] = useState(false);

  const planOptions = useMemo(
    () => [
      {
        id: 'tier1' as PlanTier,
        title: t('profile.planIndividual'),
        description: t('profile.planIndividualDescription'),
      },
      {
        id: 'tier2' as PlanTier,
        title: t('profile.planAdvanced'),
        description: t('profile.planAdvancedDescription'),
      },
      {
        id: 'tier3' as PlanTier,
        title: t('profile.planProfessional'),
        description: t('profile.planProfessionalDescription'),
      },
    ],
    [t]
  );

  const [planForm, setPlanForm] = useState<{ tier: PlanTier; billingCycle: BillingCycleOption }>({
    tier: 'tier2',
    billingCycle: '1mo',
  });

  const billingCycleOptions = useMemo(
    () => [
      { id: '1mo' as BillingCycleOption, label: t('profile.billingCycle1mo') || '1 month' },
      { id: '3mo' as BillingCycleOption, label: t('profile.billingCycle3mo') || '3 months' },
      { id: '6mo' as BillingCycleOption, label: t('profile.billingCycle6mo') || '6 months' },
    ].filter((option) => PLAN_ALLOWED_BILLING_CYCLES[planForm.tier].includes(option.id)),
    [planForm.tier, t]
  );

  const [planAcknowledged, setPlanAcknowledged] = useState(false);
  const [planLoading, setPlanLoading] = useState(false);
  const [planPricing, setPlanPricing] = useState<PlanPricingMap | null>(null);
  const [planPricingCurrency, setPlanPricingCurrency] = useState('USD');
  const [planPricingStatus, setPlanPricingStatus] = useState<PlanPricingStatus>('idle');
  // BIL-L05: what the selected change costs today and when it applies, from Stripe's preview.
  const [planPreview, setPlanPreview] = useState<PlanChangePreview | null>(null);
  const [planPreviewStatus, setPlanPreviewStatus] = useState<PlanPricingStatus>('idle');

  const loadPlanPricing = useCallback(async () => {
    if (planPricingStatus === 'loading' || planPricingStatus === 'loaded') return;
    setPlanPricingStatus('loading');
    try {
      const response = await billingAPI.pricing();
      const currency = (response.currency || 'USD').toLowerCase() as 'eur' | 'usd' | 'try';
      const map: PlanPricingMap = {};
      response.tiers?.forEach((tier) => {
        const id = tier.tier as PlanTier;
        if (tier.intervals && tier.intervals.length > 0) {
          // New v2 shape
          const cycleMap: Partial<Record<BillingCycleOption, number | null>> = {};
          tier.intervals.forEach((iv) => {
            const cycle = iv.cycle as BillingCycleOption;
            const cents = iv[currency]?.cents ?? iv.usd?.cents ?? null;
            cycleMap[cycle] = cents;
          });
          map[id] = cycleMap;
        }
      });
      setPlanPricing(map);
      setPlanPricingCurrency(response.currency || 'USD');
      setPlanPricingStatus('loaded');
    } catch (error) {
      console.warn('Failed to load pricing', error);
      setPlanPricingStatus('error');
    }
  }, [planPricingStatus]);

  useEffect(() => {
    if (showChangePlan) {
      setPlanAcknowledged(false);
      loadPlanPricing();
    }
  }, [showChangePlan, loadPlanPricing]);

  useEffect(() => {
    if (showChangePlan) {
      setPlanAcknowledged(false);
    }
  }, [planForm.tier, planForm.billingCycle, showChangePlan]);

  useEffect(() => {
    if (!showChangePlan) {
      setPlanPreview(null);
      setPlanPreviewStatus('idle');
      return;
    }
    let active = true;
    const billingCycle = normalizeBillingCycleForTier(planForm.tier, planForm.billingCycle);
    setPlanPreview(null);
    setPlanPreviewStatus('loading');
    billingAPI
      .changePlanPreview(planForm.tier, billingCycle)
      .then((preview) => {
        if (!active) return;
        setPlanPreview(preview);
        setPlanPreviewStatus('loaded');
      })
      .catch((error) => {
        if (!active) return;
        // Not blocking: the dialog falls back to generic copy and the change can still be confirmed.
        trackAuthEvent('plan_change_preview_failed', { plan_tier: planForm.tier, billing_cycle: billingCycle });
        reportHandledError(error, {
          source: 'billing.plan_change_preview',
          event: 'plan_change_preview_failed',
          plan_tier: planForm.tier,
          billing_cycle: billingCycle,
        });
        setPlanPreviewStatus('error');
      });
    return () => {
      active = false;
    };
  }, [showChangePlan, planForm.tier, planForm.billingCycle]);

  useEffect(() => {
    if (!showChangePlan) return;
    setPlanForm((prev) => {
      const nextCycle = normalizeBillingCycleForTier(prev.tier, prev.billingCycle);
      return nextCycle === prev.billingCycle ? prev : { ...prev, billingCycle: nextCycle };
    });
  }, [planForm.tier, showChangePlan]);

  /** Re-seeds the plan form from a freshly loaded profile. */
  const syncPlanForm = (data: UserProfile) => {
    const tier = normalizePlanTier(data.subscription?.plan_tier);
    setPlanForm({
      tier,
      billingCycle: normalizeBillingCycleForTier(tier, normalizeBillingCycle(undefined)),
    });
  };

  const handlePlanDetailsLink = async () => {
    trackAuthEvent('plan_details_clicked');
    await openExternalLink(t, PLAN_DETAILS_URL, t('profile.planDetailsLinkFailed'));
  };

  const redirectDeadSubscriptionToCheckout = async (
    eventName: 'plan_change_redirected_to_checkout' | 'subscription_resume_redirected_to_checkout',
  ) => {
    setShowChangePlan(false);
    try {
      await billingAPI.refresh();
    } catch {
      // The backend has already cleared the dead reference. Checkout remains
      // the recovery path even if this best-effort state refresh is unavailable.
    }
    try {
      await refreshAuth();
    } catch {
      // Profile refresh and checkout navigation still provide a recovery path.
    }
    await loadProfile();
    trackAuthEvent(eventName);
    showDialog(
      t('billing.conflicts.subscriptionGoneTitle'),
      t('billing.conflicts.subscriptionGoneMessage'),
    );
    router.push(ROUTES.GATED_PLAN_SELECTION as any);
  };

  const handleOpenChangePlan = () => {
    if (!profile) return;
    trackAuthEvent('plan_change_modal_opened', {
      current_plan_tier: profile?.subscription?.plan_tier,
    });
    setPlanForm((prev) => {
      const tier = normalizePlanTier(profile?.subscription?.plan_tier);
      return {
        tier,
        billingCycle: normalizeBillingCycleForTier(tier, prev.billingCycle),
      };
    });
    setPlanAcknowledged(false);
    setShowChangePlan(true);
  };

  const handleChangePlanSubmit = async () => {
    if (planLoading || !planAcknowledged || !planChangeCopy || planChangeCopy.kind === 'same_plan') return;
    const billingCycle = normalizeBillingCycleForTier(planForm.tier, planForm.billingCycle);
    if (billingCycle !== planForm.billingCycle) {
      setPlanForm((prev) => ({ ...prev, billingCycle }));
    }
    setPlanLoading(true);
    trackAuthEvent('plan_change_submitted', {
      plan_tier: planForm.tier,
      billing_cycle: billingCycle,
    });
    try {
      const response = await billingAPI.changePlan(
        planForm.tier,
        billingCycle,
        'always_invoice',
        planPreview?.proration_date ?? null,
      );
      setShowChangePlan(false);
      await loadProfile();
      if (response.payment_pending) {
        // BIL-L03: the charge failed or needs authentication; nothing changed yet.
        trackAuthEvent('plan_change_payment_pending', {
          plan_tier: planForm.tier,
          billing_cycle: billingCycle,
        });
        if (response.invoice_url) {
          const payNow = await confirmDialog(
            t('profile.planChangePaymentPendingTitle'),
            t('profile.planChangePaymentPendingMessage'),
            t('profile.planChangePayInvoice'),
            t('profile.cancel'),
          );
          if (payNow) {
            await openExternalLink(t, response.invoice_url, t('profile.planInvoiceLinkFailed'));
          }
        } else {
          showDialog(t('profile.planChangePaymentPendingTitle'), t('profile.planChangePaymentPendingMessage'));
        }
        return;
      }
      const buttons = [] as { text: string; onPress: () => void }[];
      if (response.invoice_url) {
        buttons.push({
          text: t('profile.viewInvoice'),
          onPress: () => openExternalLink(t, response.invoice_url!, t('profile.planInvoiceLinkFailed')),
        });
      }
      buttons.push({ text: t('common.confirm'), onPress: () => {} });
      trackAuthEvent('plan_change_succeeded', {
        plan_tier: planForm.tier,
        billing_cycle: billingCycle,
      });
      const successTitle = t('common.success');
      const successMessage = response.message || t('profile.planChangedSuccess');
      if (response.invoice_url) {
        const viewInvoice = await confirmDialog(
          successTitle,
          successMessage,
          t('profile.viewInvoice'),
          t('common.confirm'),
        );
        if (viewInvoice) {
          await openExternalLink(t, response.invoice_url, t('profile.planInvoiceLinkFailed'));
        }
      } else {
        showDialog(successTitle, successMessage, buttons);
      }
    } catch (error: any) {
      const conflict = getBillingCheckoutConflict(error);
      if (conflict?.action === 'checkout') {
        await redirectDeadSubscriptionToCheckout('plan_change_redirected_to_checkout');
        return;
      }
      const errorStatus = error?.response?.status;
      const errorType = errorStatus ? `http_${errorStatus}` : (error?.message ? 'exception' : 'unknown');
      trackAuthEvent('plan_change_failed', {
        plan_tier: planForm.tier,
        billing_cycle: billingCycle,
        error_type: errorType,
      });
      const message = error?.response?.data?.detail || t('profile.planChangeFailed');
      showDialog(t('common.error'), message);
    } finally {
      setPlanLoading(false);
    }
  };

  const formatCurrency = (amount?: number | null, currency?: string | null) =>
    formatCurrencyAmount(t, language, planPricingCurrency, amount, currency);

  const getCurrentPlanPriceLabel = () => {
    if (profile?.subscription?.amount) {
      return formatCurrency(profile.subscription.amount, profile.subscription.currency);
    }
    const fallbackTier = normalizePlanTier(profile?.subscription?.plan_tier);
    return getPlanPriceLabel(fallbackTier, planForm.billingCycle);
  };

  const planChangeCopy = planPreview
    ? describePlanChange(planPreview, {
      t,
      formatMoney: (cents, currency) => formatCurrency(cents, currency),
      formatDate: (iso) => formatDate(iso || undefined),
      currentName: planDisplayName(t, planPreview.current_plan_tier),
      targetName: planDisplayName(t, planPreview.plan_tier),
      pendingName: planPreview.pending_plan_tier ? planDisplayName(t, planPreview.pending_plan_tier) : undefined,
    })
    : planPreviewStatus === 'error'
      ? fallbackPlanChangeCopy(t)
      : null;

  const getPlanPriceLabel = (tier: PlanTier, cycle: BillingCycleOption) => {
    const cents = planPricing?.[tier]?.[cycle];
    if (typeof cents === 'number' && cents > 0) {
      return formatCurrency(cents, planPricingCurrency);
    }
    return t('profile.priceUnavailable');
  };

  return {
    showChangePlan,
    closeChangePlan: () => setShowChangePlan(false),
    handleOpenChangePlan,
    planOptions,
    billingCycleOptions,
    planForm,
    setPlanForm,
    planAcknowledged,
    setPlanAcknowledged,
    planLoading,
    planPricingStatus,
    planPreview,
    planPreviewStatus,
    planChangeCopy,
    handlePlanDetailsLink,
    handleChangePlanSubmit,
    redirectDeadSubscriptionToCheckout,
    formatCurrency,
    getCurrentPlanPriceLabel,
    getPlanPriceLabel,
    syncPlanForm,
  };
}
